/**
 * Org Strength Score — Phase 3
 *
 * Computes aggregate strength metrics for an organization based on active member profiles.
 *
 * Score field used: `duel_standard_strength_kg` — the same composite score used by
 * the org leaderboard view (public_leaderboard_org_v1) and profile scoring engine.
 *
 * Eligibility criteria (mirrors org leaderboard view):
 *   - organization_memberships.status = 'active'
 *   - profiles.onboarding_completed = true
 *   - profiles.visibility = 'public'
 *   - profiles.duel_standard_strength_kg > 0
 *
 * No gym/home_gym_id dependency — org-membership scoped only.
 * Doctrine: DUEL_CLUB_MODE_ARCHITECTURE.md
 */

import { supabase } from '@/lib/supabase';
import { createLogger } from '@/lib/logger';

const log = createLogger('ORG_STRENGTH');

// ─── Types ────────────────────────────────────────────────────────────────────

export interface OrgStrengthScore {
  /** Average duel_standard_strength_kg across eligible members. 0 if no eligible members. */
  averageStrengthScore: number;
  /** Count of members with a valid strength score (eligible for leaderboard). */
  eligibleMemberCount: number;
  /** Count of all active members (status='active'), regardless of score eligibility. */
  activeMemberCount: number;
}

export interface OrgComparisonScore {
  orgId: string;
  orgName: string;
  averageStrengthScore: number;
  memberCount: number;
}

// ─── fetchOrgStrengthScore ─────────────────────────────────────────────────────

/**
 * Compute the average strength score and eligible member count for a given org.
 *
 * Compatible with any org_id, including child orgs (team sub-orgs) since the query
 * keys off organization_memberships.org_id directly.
 *
 * @param orgId - Organization UUID
 * @returns OrgStrengthScore with averageStrengthScore=0 and counts=0 on error or no data
 */
export async function fetchOrgStrengthScore(orgId: string): Promise<OrgStrengthScore> {
  if (!orgId) {
    return { averageStrengthScore: 0, eligibleMemberCount: 0, activeMemberCount: 0 };
  }

  try {
    // Fetch active membership count (all active, regardless of score eligibility)
    const { count: activeMemberCount, error: countError } = await supabase
      .from('organization_memberships')
      .select('id', { count: 'exact', head: true })
      .eq('org_id', orgId)
      .eq('status', 'active');

    if (countError) {
      log.warn('[OrgStrength] activeMemberCount query error', countError.message);
    }

    // Fetch eligible members with their strength scores
    // Join via organization_memberships → profiles
    const { data: memberRows, error: memberError } = await supabase
      .from('organization_memberships')
      .select(`
        profiles!inner(
          duel_standard_strength_kg,
          onboarding_completed,
          visibility
        )
      `)
      .eq('org_id', orgId)
      .eq('status', 'active');

    if (memberError) {
      log.warn('[OrgStrength] eligibility query error', memberError.message);
      return {
        averageStrengthScore: 0,
        eligibleMemberCount: 0,
        activeMemberCount: activeMemberCount ?? 0,
      };
    }

    // Filter eligible: onboarding_completed, public visibility, score > 0
    const eligibleScores: number[] = (memberRows ?? [])
      .map((row: any) => row.profiles)
      .filter(
        (p: any) =>
          p &&
          p.onboarding_completed === true &&
          p.visibility === 'public' &&
          typeof p.duel_standard_strength_kg === 'number' &&
          p.duel_standard_strength_kg > 0,
      )
      .map((p: any) => p.duel_standard_strength_kg as number);

    if (eligibleScores.length === 0) {
      return {
        averageStrengthScore: 0,
        eligibleMemberCount: 0,
        activeMemberCount: activeMemberCount ?? 0,
      };
    }

    const sum = eligibleScores.reduce((acc, s) => acc + s, 0);
    const average = sum / eligibleScores.length;

    return {
      averageStrengthScore: average,
      eligibleMemberCount: eligibleScores.length,
      activeMemberCount: activeMemberCount ?? eligibleScores.length,
    };
  } catch (err: any) {
    log.error('[OrgStrength] unexpected error', err?.message ?? err);
    return { averageStrengthScore: 0, eligibleMemberCount: 0, activeMemberCount: 0 };
  }
}

// ─── fetchOrgComparisonScores ──────────────────────────────────────────────────

/**
 * Returns the top 10 organizations by average strength score, for future comparison UI.
 *
 * Only orgs with at least 1 eligible member are included.
 * Eligible: status='active', onboarding_completed=true, visibility='public', score > 0.
 *
 * NOTE: This does NOT have a UI surface yet (Phase 3). The helper is provided for
 * future school-vs-school comparison, team leaderboards, and TV Mode org boards.
 *
 * @returns Array of up to 10 orgs ordered by averageStrengthScore DESC
 */
export async function fetchOrgComparisonScores(): Promise<OrgComparisonScore[]> {
  try {
    // Fetch all active memberships with org and profile data
    const { data: rows, error } = await supabase
      .from('organization_memberships')
      .select(`
        org_id,
        organizations!inner(id, name),
        profiles!inner(
          duel_standard_strength_kg,
          onboarding_completed,
          visibility
        )
      `)
      .eq('status', 'active');

    if (error) {
      log.warn('[OrgStrength] fetchOrgComparisonScores query error', error.message);
      return [];
    }

    // Group eligible scores by org
    const orgMap = new Map<string, { name: string; scores: number[] }>();

    for (const row of (rows ?? []) as any[]) {
      const p = row.profiles;
      if (
        !p ||
        p.onboarding_completed !== true ||
        p.visibility !== 'public' ||
        typeof p.duel_standard_strength_kg !== 'number' ||
        p.duel_standard_strength_kg <= 0
      ) {
        continue;
      }

      const orgId = row.org_id as string;
      const orgName = row.organizations?.name ?? 'Unknown';

      if (!orgMap.has(orgId)) {
        orgMap.set(orgId, { name: orgName, scores: [] });
      }
      orgMap.get(orgId)!.scores.push(p.duel_standard_strength_kg);
    }

    // Compute averages and sort descending
    const results: OrgComparisonScore[] = Array.from(orgMap.entries())
      .map(([orgId, { name, scores }]) => ({
        orgId,
        orgName: name,
        averageStrengthScore: scores.reduce((a, b) => a + b, 0) / scores.length,
        memberCount: scores.length,
      }))
      .sort((a, b) => b.averageStrengthScore - a.averageStrengthScore)
      .slice(0, 10);

    return results;
  } catch (err: any) {
    log.error('[OrgStrength] fetchOrgComparisonScores unexpected error', err?.message ?? err);
    return [];
  }
}
